import { InnerQueryExpression } from "../../../../Common/CandyApiClient/Types/QueryCollecton/InnerQueryExpression";
import { NotSupportedError } from "../../../../Common/Errors";
import { AndExpression } from "../../../../Common/CandyApiClient/Types/QueryCollecton/Expressions/AndExpression";
import { OrExpression } from "../../../../Common/CandyApiClient/Types/QueryCollecton/Expressions/OrExpression";
import { NotExpression } from "../../../../Common/CandyApiClient/Types/QueryCollecton/Expressions/NotExpression";
import { StringMatchExpression } from "../../../../Common/CandyApiClient/Types/QueryCollecton/Expressions/StringMatchExpression";
import { AbsoluteModelPath, PathTokens } from "../../../../Common/ModelPath/ModelPath";
import { AbsoluteModelFieldPath } from "../../../../Common/ModelPath/AbsoluteModelFieldPath";
import { CompareExpression } from "../../../../Common/CandyApiClient/Types/QueryCollecton/Expressions/CompareExpression";
import { ValueReferenceExpression } from "../../../../Common/CandyApiClient/Types/QueryCollecton/Expressions/ValueReferenceExpression";
import { DecimalConstantExpression } from "../../../../Common/CandyApiClient/Types/QueryCollecton/Expressions/DecimalConstantExpression";
import { wrapDecimalOperand } from "../AutocalcCommonFunctions";
import { IterUtils } from "../../../../Common/IterableUtils";

import {
    FilterAndExpression,
    FilterEqualExpression,
    FilterGreaterOrEqualExpression,
    FilterLessOrEqualExpression,
    FilterOrExpression,
    FilterSubstringExpression,
    FilterUnaryNotExpression,
} from "./FilterExpression";
import { FilterExpressionTransduceVisitor } from "./CompileFilterExpression";

const notSupported: () => never = () => {
    throw new NotSupportedError();
};

export class FilterExpressionToQueryCollectionExpressionVisitor extends FilterExpressionTransduceVisitor<InnerQueryExpression> {
    private readonly multilinePath: AbsoluteModelPath;

    public constructor(multilinePath: AbsoluteModelPath) {
        super();
        this.multilinePath = multilinePath;
    }

    protected transformAndExpression(expression: FilterAndExpression): InnerQueryExpression {
        const expressions = expression.conditions.map(x => this.transformExpression(x));
        return new AndExpression(expressions);
    }

    protected transformOrExpression(expression: FilterOrExpression): InnerQueryExpression {
        const expressions = expression.conditions.map(x => this.transformExpression(x));
        return new OrExpression(expressions);
    }

    protected transformUnaryNotExpression(expression: FilterUnaryNotExpression): InnerQueryExpression {
        const expr = this.transformExpression(expression.condition);
        return new NotExpression(expr);
    }

    protected transformEqualExpression(expression: FilterEqualExpression): InnerQueryExpression {
        const fullFieldName = this.getFullFieldNameFromDataPath(
            AbsoluteModelFieldPath.createFromMask(expression.condition.dataPath, PathTokens.each)
        );
        return new StringMatchExpression(fullFieldName, expression.condition.value, "Exact", false);
    }

    protected transformSubstringExpression(expression: FilterSubstringExpression): InnerQueryExpression {
        const fullFieldName = this.getFullFieldNameFromDataPath(
            AbsoluteModelFieldPath.createFromMask(expression.condition.dataPath, PathTokens.each)
        );
        return new StringMatchExpression(fullFieldName, expression.condition.value, "Substring", false);
    }

    private getFullFieldNameFromDataPath(modelFieldPath: AbsoluteModelFieldPath): string {
        const tailPath = this.multilinePath.getSuffixPathFrom(modelFieldPath.path).trimFirstStarIfFirstToken();
        const slashOrEmptyForEndOfPath = PathTokens.isMultiToken(IterUtils.last(tailPath.getPathParts())) ? "/" : "";
        const fieldName = modelFieldPath.field;
        const fullFieldName = `${tailPath.toString()}${slashOrEmptyForEndOfPath}.${fieldName}`;
        return fullFieldName;
    }

    protected transformLessOrEqualExpression(expression: FilterLessOrEqualExpression): InnerQueryExpression {
        const fullFieldName = this.getFullFieldNameFromDataPath(
            AbsoluteModelFieldPath.createFromMask(expression.condition.dataPath, PathTokens.each)
        );
        return new CompareExpression(
            new ValueReferenceExpression(fullFieldName),
            "LessOrEquals",
            new DecimalConstantExpression(wrapDecimalOperand(expression.condition.value))
        );
    }

    protected transformGreaterOrEqualExpression(expression: FilterGreaterOrEqualExpression): InnerQueryExpression {
        const fullFieldName = this.getFullFieldNameFromDataPath(
            AbsoluteModelFieldPath.createFromMask(expression.condition.dataPath, PathTokens.each)
        );
        return new CompareExpression(
            new ValueReferenceExpression(fullFieldName),
            "GreaterOrEquals",
            new DecimalConstantExpression(wrapDecimalOperand(expression.condition.value))
        );
    }

    protected createDefaultResult(): InnerQueryExpression {
        notSupported();
    }

    protected transformByInstancesExpression(): InnerQueryExpression {
        notSupported();
    }

    protected transformEndDateExpression(): InnerQueryExpression {
        notSupported();
    }

    protected transformStartDateExpression(): InnerQueryExpression {
        notSupported();
    }
}
